import { useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  BooleanField,
  NumberField,
  BulkDeleteWithConfirmButton,
  useResourceContext,
  useTheme,
  RecordContextProvider,
  ChipField,
  FunctionField,
} from "react-admin";
import FactoryDebtPayAddDialog from "../../components/factoryDebtPayments/create/FactoryDebtPayAddDialog";
import FactoryDebtPayEditDialog from "../../components/factoryDebtPayments/edit/FactoryDebtPayEditDialog";
import factoryDebtPayFilterList from "../../components/factoryDebtPayments/filter/factoryDebtPayFilterList";
import TableListActions from "../../components/tableListActions/TableListActions";
import FactoryDebtPayExpandList from "../../components/factoryDebtPayments/expand/FactoryDebtPayExpandList";
import EditBtn from "../../components/editBtn/EditBtn";
import ConfirmBtn from "../../components/confirmBtn/ConfirmBtn";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PrintHandler from "../../components/printHandler/PrintHandler";
import PrintBtn from "../../components/printBtn/PrintBtn";
import PrintContent from "../../components/factoryDebtPayments/print/PrintContent";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import FactoryDebtPaymentFooterList from "../../components/factoryDebtPayments/footer/FactoryDebtPaymentFooterList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const FactoryDebtPaymentList = () => {
  const resource = useResourceContext();
  const { permissions, canSeeCompanyColumn, filterDefaultValues } =
    useCompanyScopedListFilters({
      start_date: firstDate,
      end_date: lastDate,
    });
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [recordId, setRecordId] = useState();
  const [resetPrintContent, setResetPrintContent] = useState();
  const [printRecord, setPrintRecord] = useState({}); // initial value wajib object kosong untuk cek key id di dependency useeffect DeliveryNote.js
  const [theme] = useTheme("light");

  const handleClickOpenAdd = () => {
    setOpenAdd(true);
  };
  const handleClickOpenEdit = (recId) => () => {
    setRecordId(recId);
    setOpenEdit(true);
  };
  const list = useMemo(
    () => (
      <List
        title="mine.list_title.factory_debt_payment"
        empty={false}
        actions={
          <TableListActions
            createHandler={handleClickOpenAdd}
            exporter={false}
            createPermission="create_factory_debt_payment"
          />
        }
        filters={factoryDebtPayFilterList(permissions)}
        queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
        filterDefaultValues={filterDefaultValues}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<FactoryDebtPaymentFooterList />} />}
          bulkActionButtons={<BulkDeleteWithConfirmButton />}
          expand={<FactoryDebtPayExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          {canSeeCompanyColumn ? (
            <TextField label="mine.label.depo" source="company.alias" />
          ) : null}
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <TextField label="Supplier" source="supplier.name" />
          <FunctionField
            textAlign="center"
            label="mine.label.type"
            render={(record) => {
              let rec = {};
              if (record.type === 1) {
                rec.type = "Transfer";
                rec.color = "primary";
              } else if (record.type === 2) {
                rec.type = "Cash";
                rec.color = "warning";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="type" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
          <NumberField source="total" />
          <TextField source="note" label="mine.label.note" />
          <BooleanField
            source="approved"
            label="mine.label.approved"
            textAlign="center"
          />
          <WrapperField label="mine.label.actions" textAlign="center">
            <WithRecord
              render={(record) => {
                return !record.approved ? (
                  <>
                    <EditBtn
                      editHandler={handleClickOpenEdit(record.id)}
                      sx={{ mr: 1 }}
                    />
                    <ConfirmBtn
                      title="mine.message.approve"
                      content="mine.message.approve_content"
                      label="mine.btn.approve"
                      resource={resource + "/approve"}
                      startIcon={<DoneIcon />}
                    />
                  </>
                ) : (
                  <>
                    {permissions && permissions.administrator && (
                      <ConfirmBtn
                        title="mine.message.unapprove"
                        content="mine.message.unapprove_content"
                        label="mine.label.unapprove"
                        resource={resource + "/unapprove"}
                        startIcon={<CloseIcon />}
                        color="error"
                      />
                    )}
                    <PrintBtn
                      dialogProps={{ title: "mine.dialog.print" }}
                      recordId={record.id}
                      setPrintRecord={setPrintRecord}
                      setResetPrintContent={setResetPrintContent}
                    />
                  </>
                );
              }}
            />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    ),
    [theme, permissions, canSeeCompanyColumn, filterDefaultValues]
  );
  return (
    <>
      {list}
      <FactoryDebtPayAddDialog open={openAdd} setOpen={setOpenAdd} />
      <FactoryDebtPayEditDialog
        open={openEdit}
        setOpen={setOpenEdit}
        recordId={recordId}
      />
      <PrintHandler
        record={printRecord}
        resetPrintContent={resetPrintContent}
        Content={PrintContent}
      />
    </>
  );
};

export default FactoryDebtPaymentList;
